/**
 * This component contains data of the last 48 hours
 * and displays around 90 points
 */
import React from 'react';
import PropTypes from 'prop-types';
import "./HistoryChart.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import moment from 'moment';
import { DATE_TIME_FORMAT } from 'constants/config';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
const N = 60;

Tooltip.positioners.MouseTooltip = function (elements, eventPosition) {
  // A reference to the tooltip model
  return {
    x: eventPosition.x,
    y: eventPosition.y
  };
};
const INIT_OPTIONS = {
  responsive: true,
  bezierCurve: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  stacked: false,
  plugins: {
    legend: {
    },
    title: {
      display: true,
      text: '汎用センサーデータ履歴',
    },
    tooltip: {
      position: 'MouseTooltip',
      // mode: 'nearest',  // Chỉ hiện tooltip khi gần điểm nhất
      intersect: true,   // Chỉ khi hover vào điểm mới hiển thị tooltip
      callbacks: {
        label: (data) => {
          const dataset = data.dataset;
          const parsedData = data.parsed?.y;
          return dataset.label + ': ' + parsedData.toFixed(2);
        },
        title: (data) => {
          return ""
        }
      }
    }
  },
  scales: {
    y1: {
      type: 'linear',
      display: true,
      position: 'left',
      title: {
        text: '',
        display: true,
      }
    },
    y2: {
      type: 'linear',
      display: true,
      position: 'right',
      grid: {
        drawOnChartArea: false,
      },
      title: {
        text: '',
        display: true,
      }
    }
  },
  elements: {
    point: {
      hitRadius: 0,
      hoverRadius: 3
    }
  }
};
// render list color of line chart
const COLOR_LIST = ['rgb(51, 102, 255)', 'rgb(255, 102, 0)', 'rgb(0, 153, 0)', 'rgb(255, 0, 0)', 'rgb(153, 0, 153)'];
const BACKGROUND_COLOR_LIST = ['rgba(51, 102, 255, 0.5)', 'rgba(255, 102, 0, 0.5)', 'rgba(0, 153, 0, 0.5)', 'rgba(255, 0, 0, 0.5)', 'rgba(153, 0, 153, 0.5)'];

const propTypes = {
  sensor: PropTypes.object,
  historyItems: PropTypes.array
};
const defaultProps = {
  sensor: {},
  historyItems: []
};

// const INIT_DATASETS = [];
const GeneralSensorHistoryChart = (props) => {
  /*** States and Variables ***/
  const txsRef = React.useRef([]);
  const listConvertValuesRef = React.useRef({});
  const [displayData, setDisplayData] = React.useState({
    labels: [],
    datasets: []
  });
  const [options, setOptions] = React.useState(INIT_OPTIONS);
  const [displayTime, setDisplayTime] = React.useState(48);

  const LIST_SIGNAL_INFO = [4, 3, 2, 1, 0].reduce((acc, i) => {
    const nameSignal = props.sensor.data[`nameSignal${i}`];
    if (!nameSignal) return acc;
    acc.push({
      signal: `a${i}`,
      nameSignal,
      minSignalValue: props.sensor.data[`numericalLowerLimitSignal${i}`],
      maxSignalValue: props.sensor.data[`numericalUpperLimitSignal${i}`],
      graphAxis: Number(props.sensor.data[`graphAxisSignal${i}`]),
      color: COLOR_LIST[i],
      backgroundColor: BACKGROUND_COLOR_LIST[i]
    });
    return acc;
  }, [])

  /*** Processing ***/
  React.useEffect(() => {
    txsRef.current = [];
    const minLeftY = props.sensor.data?.minLeftY ?? Infinity
    const maxLeftY = props.sensor.data?.maxLeftY ?? -Infinity
    const minRightY = props.sensor.data?.minRightY ?? Infinity
    const maxRightY = props.sensor.data?.maxRightY ?? -Infinity;

    for (let i = props.historyItems.length - 1; i >= 0; i--) {
      const item = props.historyItems[i];
      const tx = (new Date(item.tx)).getTime();
      txsRef.current.push(tx);
      for (let j = LIST_SIGNAL_INFO.length - 1; j >= 0; j--) {
        const { signal, minSignalValue, maxSignalValue } = LIST_SIGNAL_INFO[j];
        const value = item.data[signal] > maxSignalValue ? maxSignalValue : item.data[signal] < minSignalValue ? minSignalValue : item.data[signal];
        if (!listConvertValuesRef.current[signal]) listConvertValuesRef.current[signal] = [];
        listConvertValuesRef.current[signal].push({ x: tx, y: value });
      }
    }
    INIT_OPTIONS.scales.y1.min = minLeftY
    INIT_OPTIONS.scales.y1.max = maxLeftY
    INIT_OPTIONS.scales.y2.min = minRightY
    INIT_OPTIONS.scales.y2.max = maxRightY
    INIT_OPTIONS.plugins.tooltip.callbacks.label = (data) => {
      const datasetLabel = data.dataset.label;
      const x = data.parsed?.x;
      let pos = txsRef.current.findIndex(tx => tx >= x);
      if (pos < 0) pos = txsRef.current.length - 1;
      const decimalPartLength = props.sensor.data?.[`decimalPartLength${4 - data.datasetIndex}`] || 0;
      return datasetLabel + ': ' + data.parsed.y.toFixed(decimalPartLength);
    }
    resetData(48);
  }, [props.historyItems]);

  const resetData = (displayTime) => {
    // reset dataset
    const txs = txsRef.current;
    const datasets = [];
    for (let i = LIST_SIGNAL_INFO.length - 1; i >= 0; i--) {
      datasets[i] = {};
      datasets[i].label = LIST_SIGNAL_INFO[i].nameSignal
      datasets[i].data = [];
      datasets[i].yAxisID = LIST_SIGNAL_INFO[i].graphAxis === 1 ? 'y1' : 'y2'
      datasets[i].color = LIST_SIGNAL_INFO[i].color
      datasets[i].backgroundColor = LIST_SIGNAL_INFO[i].backgroundColor
      datasets[i].showLine = true
      datasets[i].borderDash = [4, 10, 10, 10]
    }
    const minTx = txs[txs.length - 1] - displayTime * 3600000;
    const step = (txs[txs.length - 1] - minTx) / N + 1;
    let lastPos = -1;
    const listConvertValues = listConvertValuesRef.current;
    for (let i = 0; i < N; i = i + 1) {
      // find first tx which is >= txs[0] + step * i
      const pos = txs.findIndex((tx) => ((tx >= minTx + step * i) && (tx >= minTx)));
      if (pos === lastPos) {
        continue;
      }
      if (pos >= 0) {
        for (let j = LIST_SIGNAL_INFO.length - 1; j >= 0; j--) {
          const signal = LIST_SIGNAL_INFO[j].signal;
          const convertValues = listConvertValues[signal];
          datasets[j].data.push(convertValues[pos]);
        }
      }
    }
    setDisplayData({ datasets });
    // reset option
    const newOptions = Object.assign({}, INIT_OPTIONS);
    newOptions.scales.x = {
      ticks: {
        callback: (val) => {
          const time = new Date(val);
          return "'" + moment(time).format(DATE_TIME_FORMAT).substring(2);
        }
      }
    }
    newOptions.plugins.tooltip.callbacks.title = (data) => {
      const x = data[0] && data[0].parsed?.x;
      const time = new Date(x);
      return "'" + moment(time).format(DATE_TIME_FORMAT).substring(2);
    }
    newOptions.scales.y1.title.text = props.sensor?.data?.nameLeftY
    newOptions.scales.y2.title.text = props.sensor?.data?.nameRightY
    setOptions(newOptions);
  }

  /*** Sub Components ***/
  /*** Event Handlers ***/
  /*** Main Render ***/
  return <>
    <div className='history_chart_container'>
      <select
        value={displayTime}
        onChange={(e) => { setDisplayTime(e.target.value); resetData(e.target.value) }}
        className='history_chart_time_select'>
        <option value={72}>72時間</option>
        <option value={48}>48時間</option>
        <option value={24}>24時間</option>
        <option value={12}>12時間</option>
        <option value={6}>06時間</option>
        <option value={3}>03時間</option>
        <option value={1}>01時間</option>
      </select>
    </div>
    <Scatter options={options} data={displayData} />
  </>;
}
GeneralSensorHistoryChart.propTypes = propTypes;
GeneralSensorHistoryChart.defaultProps = defaultProps;
export default GeneralSensorHistoryChart;